"use client";

import { Sidebar } from "flowbite-react";
import {
  HiOutlineChartPie,
  HiOutlineCog,
  HiOutlineClipboardList,
  HiOutlineUsers,
  HiOutlineNewspaper,
  HiOutlineCalendar,
  HiOutlineTemplate,
  HiOutlineLightBulb,
  HiOutlineCollection,
  HiOutlineSparkles,
  HiOutlineGift,
} from "react-icons/hi";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleSideNavPosts,
  toggleSideNavRemoteConfig,
  toggleSideNavAi,
  toggleSideNavCatalog,
} from "../../redux/slices/config";
import { Link } from "react-router-dom";
import { reviewInboxPath } from "../../helpers/reviewFilters";

const customTheme = {
  root: {
    base: "bg-white p-3",
    inner: "bg-white",
  },
};

function SectionLabel({ children }) {
  return (
    <p className="px-2 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
      {children}
    </p>
  );
}

const SideNav = () => {
  const { sideNav } = useSelector((state) => state.config);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const expandPosts = sideNav.expandPosts;
  const expandRemoteConfig = sideNav.expandRemoteConfig;
  const expandAi = sideNav.expandAi;
  const expandCatalog = sideNav.expandCatalog;

  return (
    <aside
      className="fixed top-0 left-0 z-40 w-64 h-screen pt-14 transition-transform -translate-x-full bg-white border-r border-gray-200 md:translate-x-0 dark:bg-gray-800 dark:border-gray-700 overflow-y-auto"
      aria-label="Sidenav"
      id="drawer-navigation"
    >
      <Sidebar
        aria-label="Admin navigation"
        theme={customTheme}
      >
        <Sidebar.Items>
          <Sidebar.ItemGroup>
            <SectionLabel>Overview</SectionLabel>
            <Link to="/">
              <Sidebar.Item icon={HiOutlineChartPie}>Dashboard</Sidebar.Item>
            </Link>
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            <SectionLabel>Catalog</SectionLabel>
            <Sidebar.Collapse
              open={expandCatalog}
              icon={HiOutlineCollection}
              label="Pipeline"
              onClick={() => dispatch(toggleSideNavCatalog())}
            >
              <Link to={reviewInboxPath()}>
                <Sidebar.Item>Review inbox</Sidebar.Item>
              </Link>
              <Link to="/crawler">
                <Sidebar.Item>Crawler health</Sidebar.Item>
              </Link>
              <Link to="/coverage">
                <Sidebar.Item>Coverage</Sidebar.Item>
              </Link>
              <Link to="/reports">
                <Sidebar.Item>Reports</Sidebar.Item>
              </Link>
            </Sidebar.Collapse>

            <Sidebar.Collapse
              open={expandPosts}
              icon={HiOutlineNewspaper}
              label="Posts"
              onClick={() => dispatch(toggleSideNavPosts())}
            >
              <Link to="/article">
                <Sidebar.Item>Articles</Sidebar.Item>
              </Link>
              <Link to="/school">
                <Sidebar.Item>Schools</Sidebar.Item>
              </Link>
              <Link to="/scholarship">
                <Sidebar.Item>Scholarships</Sidebar.Item>
              </Link>
              <Link to="/country">
                <Sidebar.Item>Countries</Sidebar.Item>
              </Link>
            </Sidebar.Collapse>
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            <SectionLabel>AI</SectionLabel>
            <Sidebar.Collapse
              open={expandAi}
              icon={HiOutlineLightBulb}
              label="Advisor tools"
              onClick={() => dispatch(toggleSideNavAi())}
            >
              <Link to="/ai-advisor">
                <Sidebar.Item>Study Advisor</Sidebar.Item>
              </Link>
              <Link to="/ai-completeness">
                <Sidebar.Item>Completeness</Sidebar.Item>
              </Link>
              {user && user.role === "admin" && (
                <>
                  <Link to="/ai-usage">
                    <Sidebar.Item>Usage</Sidebar.Item>
                  </Link>
                  <Link to="/ai-config">
                    <Sidebar.Item>Config</Sidebar.Item>
                  </Link>
                  <Link to="/ai-conversations">
                    <Sidebar.Item>Conversations</Sidebar.Item>
                  </Link>
                </>
              )}
            </Sidebar.Collapse>
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            <SectionLabel>Operations</SectionLabel>
            <Link to="/applications">
              <Sidebar.Item icon={HiOutlineClipboardList}>
                Applications
              </Sidebar.Item>
            </Link>
            <Link to="/templates">
              <Sidebar.Item icon={HiOutlineTemplate}>Templates</Sidebar.Item>
            </Link>
            {user && user.role === "admin" && (
              <Link to="/calendar">
                <Sidebar.Item icon={HiOutlineCalendar}>Calendar</Sidebar.Item>
              </Link>
            )}
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            <SectionLabel>People</SectionLabel>
            <Link to="/users">
              <Sidebar.Item icon={HiOutlineUsers}>Users</Sidebar.Item>
            </Link>
            <Link to="/ambassadors">
              <Sidebar.Item icon={HiOutlineSparkles}>Creators</Sidebar.Item>
            </Link>
            <Link to="/referrals">
              <Sidebar.Item icon={HiOutlineGift}>Referrals</Sidebar.Item>
            </Link>
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            <SectionLabel>System</SectionLabel>
            <Sidebar.Collapse
              open={expandRemoteConfig}
              icon={HiOutlineCog}
              label="Remote Config"
              onClick={() => dispatch(toggleSideNavRemoteConfig())}
            >
              <Link to="/quick-search">
                <Sidebar.Item>Quick Search</Sidebar.Item>
              </Link>
            </Sidebar.Collapse>
          </Sidebar.ItemGroup>
        </Sidebar.Items>
      </Sidebar>
    </aside>
  );
};

export default SideNav;
